import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ADMIN_ROLES, PAYROLL_READ_ROLES, PAYROLL_WRITE_ROLES } from "@somasi/shared";
import { api } from "@/integrations/aws/resources";
import { PageHeader, type Crumb } from "@/components/app-shell";
import { PersonForm } from "@/components/person-form";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Mail, UserX } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";

/**
 * `/people/{id}` — one person's record.
 *
 * A page rather than a modal because of what hangs off it: the terms, the leave
 * allowances, the net preview, the documents, and the roles their account holds.
 * It is also the thing somebody links a colleague to, which a dialog cannot be.
 *
 * The two account actions live up here rather than in the form. Inviting a
 * sign-in and taking one away are not field edits — they happen immediately and
 * they are not undone by cancelling a form.
 */
export const Route = createFileRoute("/_authenticated/people/$personId")({
  head: () => ({ meta: [{ title: "Person — Somasi Ops" }] }),
  component: PersonPage,
});

/** Back to the register, which is where every person page is reached from. */
const PEOPLE_CRUMB: Crumb = { label: "People", to: "/people" };

function PersonPage() {
  const { personId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const { hasAny, user: currentUser } = useAuth();

  const canWrite = hasAny(PAYROLL_WRITE_ROLES);
  const canSeeTerms = hasAny(PAYROLL_READ_ROLES);
  const canGrant = hasAny(ADMIN_ROLES);

  const person = useQuery({
    queryKey: ["people", personId],
    // Not asked for at all by somebody who may not see the answer.
    enabled: canWrite,
    queryFn: async () => {
      const result = await api.employees.get(personId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["people"] });
    void person.refetch();
  };

  const invite = useMutation({
    mutationFn: async () => {
      const result = await api.employees.inviteAccount(personId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (account) => {
      notify.success(`Invitation sent to ${account?.email ?? "their address"}`);
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const removeAccount = useMutation({
    mutationFn: async () => {
      const result = await api.employees.removeAccount(personId);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Sign-in removed; the record stays");
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  /*
   * The same check the sidebar makes before drawing the row, and the register
   * makes before listing anybody. Repeated here because a record is linkable:
   * hiding the entry does not stop a URL arriving from a colleague.
   */
  if (!canWrite) {
    return (
      <div className="space-y-6">
        <PageHeader title="Person" breadcrumbs={[PEOPLE_CRUMB, { label: "Person" }]} />
        <Alert variant="destructive">
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            Only the payroll roles may open somebody's record. Your own days, leave and payslips are
            under Attendance, Leave and Payslips.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (person.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Person" breadcrumbs={[PEOPLE_CRUMB, { label: "Person" }]} />
        <p className="text-muted-foreground text-sm">Reading their record…</p>
      </div>
    );
  }

  if (person.error || !person.data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Person" breadcrumbs={[PEOPLE_CRUMB, { label: "Person" }]} />
        <Alert variant="destructive">
          <AlertTitle>Cannot read this record</AlertTitle>
          <AlertDescription>
            {person.error ? (person.error as Error).message : "Nobody here has that id."}
          </AlertDescription>
        </Alert>
        <Button variant="outline" asChild>
          <Link to="/people">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to People
          </Link>
        </Button>
      </div>
    );
  }

  const row = person.data;
  const isSelf = row.account?.id === currentUser?.id;

  return (
    <div className="space-y-6">
      <PageHeader
        title={row.full_name}
        breadcrumbs={[PEOPLE_CRUMB, { label: row.full_name }]}
        description={
          [row.job_title, row.account?.email].filter(Boolean).join(" · ") || "No title recorded"
        }
        actions={
          <div className="flex items-center gap-2">
            {!row.account?.confirmed && row.account && (
              <Badge variant="outline" className="text-[10px]">
                invited, not signed in
              </Badge>
            )}
            {isSelf && (
              <Badge variant="outline" className="text-[10px]">
                you
              </Badge>
            )}

            {canWrite && !row.user_id && row.email && (
              <Button variant="outline" disabled={invite.isPending} onClick={() => invite.mutate()}>
                <Mail className="mr-2 h-4 w-4" />
                Invite to sign in
              </Button>
            )}

            {canGrant && row.user_id && !isSelf && (
              <Button
                variant="outline"
                onClick={async () => {
                  const ok = await confirmDelete(
                    `${row.full_name}'s sign-in`,
                    "The account and its roles go; the record stays, because their payslips " +
                      "are still records. They can be invited again later.",
                  );
                  if (ok) removeAccount.mutate();
                }}
              >
                <UserX className="mr-2 h-4 w-4" />
                Remove sign-in
              </Button>
            )}

            <Button variant="outline" asChild>
              <Link to="/people">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Link>
            </Button>
          </div>
        }
      />

      {!row.user_id && row.employment_type !== "deemed_employee" && (
        <Alert>
          <AlertTitle>No sign-in yet</AlertTitle>
          <AlertDescription>
            Until they have an account their attendance page is empty, they cannot read their own
            payslip, and no role can be granted — there is nothing to grant it to.
          </AlertDescription>
        </Alert>
      )}

      <PersonForm
        person={row}
        canSeeTerms={canSeeTerms}
        canGrant={canGrant}
        onCancel={() => void navigate({ to: "/people" })}
        onSaved={() => refresh()}
      />
    </div>
  );
}
